// Phase 26E.2 — Admin Claim (TA/DA) Grid.
// ERP-style claim approval grid consuming the shared BulkActionGrid from
// 26E.1. RM viewer sees stage-1-pending claims by default; Admin viewer sees
// stage-2-pending. Approval-Status filter widens to view approved/rejected
// history read-only.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Check, X, Download, ChevronDown, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import BulkActionGrid from "@/components/BulkActionGrid";

const TESTID = "admin-claim-grid";

const STATUS_STYLES = {
  submitted:         "bg-blue-100 text-blue-700",
  manager_approved:  "bg-amber-100 text-amber-700",
  admin_approved:    "bg-emerald-100 text-emerald-700",
  rejected:          "bg-red-100 text-red-700",
  draft:             "bg-neutral-200 text-neutral-700",
};
const STAGE_LABEL = { user: "0", manager: "1 · RM", admin: "2 · Admin", closed: "Closed" };

export default function AdminClaimGrid() {
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(new Set());   // claim_id set
  const [itemsCache, setItemsCache] = useState({});      // claim_id → items[]
  const [overrides, setOverrides] = useState({});        // claim_id → { item_id: amount }

  useEffect(() => {
    Promise.all([
      api.get("/zones").catch(() => ({ data: { items: [] } })),
      api.get("/sub-zones").catch(() => ({ data: { items: [] } })),
      api.get("/users", { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
    ]).then(([z, sz, u]) => {
      setZones(z.data.items || []);
      setSubZones(sz.data.items || []);
      setUsers(u.data.items || []);
    });
  }, []);

  const toggleExpand = async (claim) => {
    const cid = claim.id;
    const next = new Set(expanded);
    if (next.has(cid)) {
      next.delete(cid);
    } else {
      next.add(cid);
      if (!itemsCache[cid]) {
        try {
          const { data } = await api.get(`/tada/claims/${cid}/full`);
          setItemsCache(prev => ({ ...prev, [cid]: data.items || [] }));
        } catch (e) { toast.error("Could not load items"); }
      }
    }
    setExpanded(next);
  };

  const setOverride = (cid, item_id, amount, maxAmount) => {
    const clean = amount === "" ? null : Math.max(0, Math.min(Number(amount) || 0, maxAmount));
    setOverrides(prev => {
      const cur = { ...(prev[cid] || {}) };
      if (clean === null || clean === maxAmount) delete cur[item_id];
      else cur[item_id] = clean;
      const next = { ...prev };
      if (Object.keys(cur).length === 0) delete next[cid]; else next[cid] = cur;
      return next;
    });
  };

  const filters = useMemo(() => ([
    { key: "statuses", type: "checkbox_group", label: "Status",
      options: [
        { value: "submitted",        label: "Stage 1 (RM)" },
        { value: "manager_approved", label: "Stage 2 (Admin)" },
        { value: "admin_approved",   label: "Approved" },
        { value: "rejected",         label: "Rejected" },
      ] },
    { key: "date_range", type: "date_range", label: "Claim Date" },
    { key: "zone_ids",  type: "single_select", label: "Zone",
      options: zones.map(z => ({ value: z.id, label: z.code || z.name })) },
    { key: "sub_zone_id", type: "single_select", label: "Sub-Zone",
      options: subZones.map(z => ({ value: z.id, label: z.code || z.name })) },
    { key: "role_codes", type: "single_select", label: "Role",
      options: ["SE", "ASM", "ZSM", "RSM", "SALES_HEAD"].map(r => ({ value: r, label: r })) },
    { key: "user_ids", type: "single_select", label: "User",
      options: users.map(u => ({ value: u.id, label: u.name })) },
  ]), [zones, subZones, users]);

  const columns = useMemo(() => ([
    { key: "expand", label: "", width: 32,
      render: (r) => (
        <button onClick={(e) => { e.stopPropagation(); toggleExpand(r); }}
                className="text-neutral-500 hover:text-neutral-900 p-0.5"
                data-testid={`${TESTID}-expand-${r.id}`}>
          {expanded.has(r.id) ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      ) },
    { key: "id", label: "Claim ID", width: 130,
      render: (r) => <span className="font-mono text-[11px] text-neutral-500">{r.id.slice(0, 8)}…</span> },
    { key: "user_name", label: "Submitter", width: 140 },
    { key: "user_role", label: "Role", width: 70,
      render: (r) => <span className="text-[11px] text-neutral-600">{r.user_role || "—"}</span> },
    { key: "claim_date", label: "Claim Date", width: 100 },
    { key: "total_claimed_inr", label: "Claim ₹", align: "right", width: 90,
      render: (r) => `₹${Number(r.total_claimed_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
    { key: "total_approved_inr", label: "Approve ₹", align: "right", width: 90,
      render: (r) => `₹${Number(r.total_approved_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
    { key: "status", label: "Status", width: 130,
      render: (r) => (
        <span className={"inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider " + (STATUS_STYLES[r.status] || "bg-neutral-200 text-neutral-700")}
              data-testid={`${TESTID}-status-${r.id}`}>
          {r.status?.replace("_", " ")}
        </span>
      ) },
    { key: "current_stage", label: "Stage", width: 80,
      render: (r) => <span className="text-[11px] font-mono">{STAGE_LABEL[r.current_stage] || r.current_stage}</span> },
    // Phase 26E.2 policy chip cluster.
    { key: "policy_context", label: "Policy", width: 240,
      render: (r) => <PolicyChips ctx={r.policy_context} testid={`${TESTID}-policy-${r.id}`} /> },
  ]), [expanded]);

  const onLoad = async (query) => {
    const params = { ...query, enrich: "grid" };
    if (params.date_range) {
      Object.assign(params, params.date_range);
      delete params.date_range;
    }
    Object.keys(params).forEach(k => { if (params[k] === "" || params[k] == null) delete params[k]; });
    ["statuses", "zone_ids", "role_codes", "user_ids"].forEach(k => {
      if (Array.isArray(params[k])) params[k] = params[k].join(",");
    });
    const { data } = await api.get("/tada/approval", { params });
    return { rows: data.items || [], total: data.total || 0, page_size: data.page_size };
  };

  const bulkActions = useMemo(() => ([
    {
      key: "approve",
      label: "Approve",
      icon: Check,
      appliesTo: (rows) => rows.some(r => r.status === "submitted" || r.status === "manager_approved"),
      count: (rows) => rows.filter(r => r.status === "submitted" || r.status === "manager_approved").length,
      onExecute: async (rows) => {
        const eligible = rows.filter(r => r.status === "submitted" || r.status === "manager_approved");
        const claim_ids = eligible.map(r => r.id);
        // Build per-item overrides payload from the current UI edits.
        const ov = {};
        for (const cid of claim_ids) {
          if (overrides[cid] && Object.keys(overrides[cid]).length) {
            ov[cid] = { items: overrides[cid] };
          }
        }
        const payload = { claim_ids, overrides: ov, comment: "Bulk approve from admin grid" };
        // 26E.2 fix-pass hardening — the tester reported a false-positive
        // success toast. Explicit console trace makes any future silent
        // failure obvious in devtools; defensive response guards make
        // "reading from response.applied.length" impossible to spoof from
        // client-side selection state.
        // eslint-disable-next-line no-console
        console.info("[26E.2] bulk-approve POST", payload);
        const resp = await api.post("/tada/approval/bulk-approve", payload);
        const data = resp?.data;
        // eslint-disable-next-line no-console
        console.info("[26E.2] bulk-approve response", { status: resp?.status, body: data });
        if (!data || !Array.isArray(data.applied) || !Array.isArray(data.skipped)) {
          toast.error("Unexpected response shape from server — no changes made");
          throw new Error("bulk-approve returned unexpected shape");
        }
        setOverrides({});
        return { toast: `Approved ${data.applied.length} · Skipped ${data.skipped.length}` };
      },
    },
    {
      key: "reject",
      label: "Reject",
      icon: X,
      destructive: true,
      appliesTo: (rows) => rows.some(r => r.status === "submitted" || r.status === "manager_approved"),
      count: (rows) => rows.filter(r => r.status === "submitted" || r.status === "manager_approved").length,
      requiresPrompt: true,
      promptDescription: "Enter a rejection reason. It will be recorded on every selected claim's approval log.",
      promptSchema: [{ key: "reason", label: "Rejection Reason", type: "textarea",
                        required: true, minLength: 3,
                        placeholder: "e.g. Policy monthly cap exceeded" }],
      confirmLabel: "Reject selected",
      onExecute: async (rows, values) => {
        const claim_ids = rows.filter(r => r.status === "submitted" || r.status === "manager_approved").map(r => r.id);
        const payload = { claim_ids, reason: values.reason.trim() };
        // eslint-disable-next-line no-console
        console.info("[26E.2] bulk-reject POST", payload);
        const resp = await api.post("/tada/approval/bulk-reject", payload);
        const data = resp?.data;
        // eslint-disable-next-line no-console
        console.info("[26E.2] bulk-reject response", { status: resp?.status, body: data });
        if (!data || !Array.isArray(data.applied) || !Array.isArray(data.skipped)) {
          toast.error("Unexpected response shape from server — no changes made");
          throw new Error("bulk-reject returned unexpected shape");
        }
        return { toast: `Rejected ${data.applied.length} · Skipped ${data.skipped.length}` };
      },
    },
    {
      key: "download",
      label: "Download",
      icon: Download,
      appliesTo: () => true,
      onExecute: async () => {
        const { data } = await api.get("/tada/approval/export",
          { params: { format: "xlsx" }, responseType: "blob" });
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url; a.download = "claims-approval.xlsx"; a.click();
        return { toast: "Downloaded claims export" };
      },
    },
  ]), [overrides]);

  // Render an expanded row panel below every expanded claim row. Rather than
  // interleave into the BulkActionGrid <tbody>, we render an accessory card
  // below the grid that lists the items for currently-expanded claim_ids.
  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid={`${TESTID}-page`}>
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Claims" }]}
        title="Claim Approvals"
        subtitle="Review travel and DA claims and approve, reject or download them — one at a time or many at once."
        favorite={{ key: "admin.claims.grid", type: "route", label: "Admin Claim Grid", route: "/admin/claims/grid" }}
        testid={`${TESTID}-page-header`}
      />
      <BulkActionGrid
        testid={TESTID}
        filters={filters}
        columns={columns}
        onLoad={onLoad}
        bulkActions={bulkActions}
        rowKey="id"
      />
      {/* Expanded item panels appear below the grid as accessory cards. */}
      {[...expanded].map(cid => {
        const items = itemsCache[cid] || [];
        return (
          <div key={cid} className="border border-neutral-200 rounded-2xl bg-white p-3 space-y-2"
               data-testid={`${TESTID}-expanded-${cid}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-neutral-700">
                Claim <span className="font-mono">{cid.slice(0, 8)}…</span> · Items
              </div>
              <button onClick={() => toggleExpand({ id: cid })}
                      className="text-[11px] text-neutral-500 hover:text-neutral-900">Collapse</button>
            </div>
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] text-neutral-500 border-b border-neutral-200">
                <tr>
                  <th className="py-1">Type</th>
                  <th>Bill Date</th>
                  <th className="text-right">Claim ₹</th>
                  <th className="text-right">Approve ₹ (editable)</th>
                  <th>Voucher</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-3 text-neutral-500">Loading…</td></tr>
                ) : items.map(it => {
                  const claimAmt = Number(it.computed_amount_inr || it.expense_amount_inr || it.da_amount_inr || 0);
                  const current = (overrides[cid] || {})[it.id];
                  return (
                    <tr key={it.id} className="border-t border-neutral-100">
                      <td className="py-1.5 font-medium capitalize">{it.item_type}</td>
                      <td>{it.bill_date || "—"}</td>
                      <td className="text-right font-mono">₹{claimAmt.toFixed(2)}</td>
                      <td className="text-right">
                        <input type="number" step="any" min="0" max={claimAmt}
                                value={current ?? claimAmt}
                                onChange={e => setOverride(cid, it.id, e.target.value, claimAmt)}
                                className="w-24 h-7 rounded border border-neutral-200 px-1.5 text-right font-mono"
                                data-testid={`${TESTID}-override-${cid}-${it.id}`} />
                      </td>
                      <td className="text-[10px]">{it.voucher_number || "—"}</td>
                      <td className="text-[10px] text-neutral-500 truncate max-w-[200px]">{it.item_comment || it.expense_description || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}


function PolicyChips({ ctx, testid }) {
  if (!ctx) return <span className="text-[10px] text-neutral-400">—</span>;
  const capUse = ctx.cap_usage_pct;
  const overCap = ctx.over_cap;
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-[#6D2C91] font-mono font-semibold"
            title={ctx.group_name}>
        {ctx.group_code}
      </span>
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">
        {ctx.rate_hint}
      </span>
      {ctx.monthly_cap_inr > 0 ? (
        <span className={"text-[10px] px-1.5 py-0.5 rounded font-mono " +
                            (overCap ? "bg-red-100 text-red-700" : "bg-emerald-50 text-emerald-700")}
              title={`This claim: ₹${(ctx.this_claim_total_inr || 0).toFixed(0)} · Month-to-date: ₹${(ctx.monthly_consumed_inr || 0).toFixed(0)} · Cap: ₹${(ctx.monthly_cap_inr || 0).toFixed(0)}`}>
          {capUse != null ? `${capUse.toFixed(0)}% of cap` : "cap"}
        </span>
      ) : (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">no cap</span>
      )}
    </div>
  );
}
